import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FaArrowLeft,
  FaCheckCircle,
  FaExclamationTriangle,
  FaSpinner,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { authService } from '../../../../services/apiService';
import { shelfListingService } from '../../../../services/shelfListingService';
import {
  isRestrictedPartCategory,
} from '../../../../utils/shelfListingConstants';
import { minPhotosForGrade } from '../../../../utils/partListingShots';
import { getGradeMeta } from '../../../../utils/usedPartGrades';
import UsedGradeCardSelector from '../../../vendor/UsedGradeCardSelector';
import FitmentAttrsForm from '../components/FitmentAttrsForm';
import ShelfListingStepper from '../components/ShelfListingStepper';
import OemLabelCapture from '../components/OemLabelCapture';
import { SuggestedField } from '../components/SuggestedField';
import { AVAILABILITY_TIERS } from '../../../../utils/availabilityTiers';

const FUEL_TYPES = ['Petrol', 'Diesel', 'CNG', 'Electric', 'Hybrid', 'Dual Fuel'];
const TRANSMISSIONS = [
  'Manual 5-speed',
  'Manual 6-speed',
  'AMT',
  'CVT',
  'Torque Converter AT',
  'DCT',
  'iMT',
];

export default function ShelfPartListingForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { shots = [], sessionId } = location.state || {};

  const [loadingSuggest, setLoadingSuggest] = useState(true);
  const [profile, setProfile] = useState('B');
  const [suggestedFields, setSuggestedFields] = useState(new Set());
  const [grade, setGrade] = useState('');
  const [partName, setPartName] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState('');
  const [fuelType, setFuelType] = useState('');
  const [transmission, setTransmission] = useState('');
  const [fitmentAttrs, setFitmentAttrs] = useState({});
  const [fitmentErrors, setFitmentErrors] = useState({});
  const [description, setDescription] = useState('');
  const [availabilityTier, setAvailabilityTier] = useState('LIKELY');
  const [isOemOriginal, setIsOemOriginal] = useState(false);
  const [oemPhotoUrl, setOemPhotoUrl] = useState('');
  const [oemDowngradeMsg, setOemDowngradeMsg] = useState('');
  const [provenanceSourceUrl, setProvenanceSourceUrl] = useState('');
  const [provenanceVinUrl, setProvenanceVinUrl] = useState('');
  const [sellerDeclaration, setSellerDeclaration] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const imageUrls = shots.map((s) => s.imageUrl || s.previewUrl).filter(Boolean);

  const loadSuggestions = useCallback(async () => {
    try {
      setLoadingSuggest(true);
      const data = await shelfListingService.suggest({
        shots,
        sessionId,
      });
      if (!data) return;
      setProfile(data.profile || 'B');
      setPartName(data.partName || '');
      setMake(data.vehicle?.make || '');
      setModel(data.vehicle?.model || '');
      setYear(data.vehicle?.year || data.vehicle?.generation || '');
      setFuelType(data.vehicle?.fuelType || '');
      setTransmission(data.vehicle?.transmission || '');
      setFitmentAttrs(data.fitmentAttrs || {});
      setSuggestedFields(new Set(data.suggestedFields || []));
    } catch {
      toast.error('Could not load AI suggestions — fill in manually');
    } finally {
      setLoadingSuggest(false);
    }
  }, [shots, sessionId]);

  useEffect(() => {
    if (shots.length >= 3) loadSuggestions();
  }, [shots.length, loadSuggestions]);

  const provenanceRequired = useMemo(() => {
    return isRestrictedPartCategory(partName);
  }, [partName]);

  if (!shots.length || shots.length < 3) {
    return (
      <div className="max-w-md mx-auto py-16 text-center px-4">
        <p className="text-slate-600">Complete the photo sequence first.</p>
        <button
          type="button"
          onClick={() => navigate('/vendor/dashboard/parts/add')}
          className="mt-4 text-emerald-700 font-semibold text-sm"
        >
          Open camera →
        </button>
      </div>
    );
  }

  const validateFitment = async () => {
    const fieldsRes = await (await import('../../../../services/apiService')).catalogNodeService.getFitmentFields(profile);
    const fields = fieldsRes?.data || [];
    const errors = {};
    fields.forEach((f) => {
      if (f.required && !String(fitmentAttrs[f.key] ?? '').trim()) {
        errors[f.key] = `${f.label} is required`;
      }
    });
    setFitmentErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!partName.trim()) errors.partName = 'Part name is required';
    if (!make.trim()) errors.make = 'Make is required';
    if (!model.trim()) errors.model = 'Model is required';
    if (!year.trim()) errors.year = 'Year is required';
    if (!fuelType) errors.fuelType = 'Fuel type is required';
    if (!transmission) errors.transmission = 'Transmission is required';
    if (!grade) errors.grade = 'Select a grade';
    if (grade && imageUrls.length < minPhotosForGrade(grade)) {
      errors.grade = `Grade ${grade} requires ${minPhotosForGrade(grade)} photos — add the functional surfaces shot`;
    }
    if (isOemOriginal && !oemPhotoUrl) errors.oem = 'OEM label photo is required';
    if (provenanceRequired) {
      if (!provenanceSourceUrl) errors.provenanceSource = 'Source document required';
      if (!sellerDeclaration) errors.declaration = 'Seller declaration is required';
    }
    setFieldErrors(errors);
    const fitmentOk = await validateFitment();
    if (Object.keys(errors).length || !fitmentOk) {
      toast.error('Fix highlighted fields before continuing');
      return;
    }

    navigate('/vendor/dashboard/parts/add/publish', {
      state: {
        shots,
        sessionId,
        partName: partName.trim(),
        vehicle: {
          make: make.trim(),
          model: model.trim(),
          generation: year.trim(),
          year: year.trim(),
          fuelType,
          transmission,
          variant: 'ALL',
        },
        fitmentAttrs,
        grade,
        profile,
        description,
        availabilityTier,
        isOemOriginal,
        oemPhotoUrl: isOemOriginal ? oemPhotoUrl : undefined,
        oemClaimAttempt: oemPhotoUrl ? 2 : 1,
        oemDowngradeMsg,
        provenanceRequired,
        provenancePack: provenanceRequired
          ? {
              sourceDocumentUrl: provenanceSourceUrl,
              vinEvidenceUrl: provenanceVinUrl || undefined,
              sellerDeclarationSigned: sellerDeclaration,
            }
          : undefined,
      },
    });
  };

  const uploadDoc = async (file, setter) => {
    try {
      const url = await authService.uploadCompressedFile(file);
      setter(url?.path || url?.url || url);
    } catch {
      toast.error('Upload failed');
    }
  };

  return (
    <div className="max-w-2xl mx-auto py-4 px-4 pb-24">
      <button
        type="button"
        onClick={() => navigate('/vendor/dashboard/parts/add', { state: { shots, sessionId } })}
        className="text-sm text-slate-500 hover:text-slate-800 mb-3 flex items-center gap-1"
      >
        <FaArrowLeft /> Retake photos
      </button>

      <ShelfListingStepper current={2} />

      <h2 className="text-xl font-bold text-slate-900">Confirm part details</h2>
      <p className="text-sm text-slate-500 mt-1">Click 2 — verify AI suggestions and complete fitment</p>

      <div className="grid grid-cols-4 gap-2 mt-4 mb-6">
        {imageUrls.map((url, i) => (
          <img key={i} src={url} alt="" className="rounded-lg aspect-square object-cover border border-slate-200" />
        ))}
      </div>

      {loadingSuggest ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 mb-6">
          <FaSpinner className="animate-spin" /> Analysing photos…
        </div>
      ) : null}

      <form onSubmit={handleSubmit} className="space-y-6">
        <section className="space-y-3">
          <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide">Vehicle & part</h3>
          <SuggestedField
            label="Part name"
            suggested={suggestedFields.has('partName')}
            required
            error={fieldErrors.partName}
          >
            <input
              type="text"
              value={partName}
              onChange={(e) => setPartName(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          </SuggestedField>
          <div className="grid grid-cols-2 gap-3">
            <SuggestedField label="Make" suggested={suggestedFields.has('make')} required error={fieldErrors.make}>
              <input
                type="text"
                value={make}
                onChange={(e) => setMake(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
            </SuggestedField>
            <SuggestedField label="Model" suggested={suggestedFields.has('model')} required error={fieldErrors.model}>
              <input
                type="text"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
            </SuggestedField>
            <SuggestedField label="Year" suggested={suggestedFields.has('year')} required error={fieldErrors.year}>
              <input
                type="text"
                inputMode="numeric"
                value={year}
                onChange={(e) => setYear(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
            </SuggestedField>
            <SuggestedField
              label="Fuel type"
              suggested={suggestedFields.has('fuelType')}
              required
              error={fieldErrors.fuelType}
            >
              <select
                value={fuelType}
                onChange={(e) => setFuelType(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              >
                <option value="">Select</option>
                {FUEL_TYPES.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
            </SuggestedField>
            <SuggestedField
              label="Transmission"
              suggested={suggestedFields.has('transmission')}
              required
              error={fieldErrors.transmission}
              className="col-span-2"
            >
              <select
                value={transmission}
                onChange={(e) => setTransmission(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              >
                <option value="">Select</option>
                {TRANSMISSIONS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </SuggestedField>
          </div>
        </section>

        <section>
          <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide mb-2">Fitment attributes</h3>
          <FitmentAttrsForm
            partCategory={partName}
            profile={profile}
            value={fitmentAttrs}
            onChange={setFitmentAttrs}
            fieldErrors={fitmentErrors}
            vehiclePath={{
              make,
              model,
              generation: year,
              fuelType,
              transmission,
            }}
          />
        </section>

        <section>
          <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide mb-2">Grade</h3>
          {fieldErrors.grade && (
            <p className="text-xs text-red-600 mb-2 flex items-center gap-1">
              <FaExclamationTriangle /> {fieldErrors.grade}
              {grade === 'C' && imageUrls.length < 4 && (
                <button
                  type="button"
                  className="underline ml-1"
                  onClick={() =>
                    navigate('/vendor/dashboard/parts/add', { state: { shots, sessionId, captureShot4: true } })
                  }
                >
                  Add photo
                </button>
              )}
            </p>
          )}
          <UsedGradeCardSelector
            value={grade}
            onChange={({ usedGrade }) => setGrade(usedGrade)}
            photoCount={imageUrls.length}
          />
          {grade && (
            <p className="text-xs text-slate-500 mt-2">
              {getGradeMeta(grade).label} · min {minPhotosForGrade(grade)} photos ·{' '}
              {imageUrls.length} captured
            </p>
          )}
        </section>

        <section className="space-y-3">
          <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide">OEM claim (C-F6)</h3>
          <p className="text-sm text-slate-600">Is this OEM original?</p>
          <div className="flex gap-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                name="oem-original"
                checked={isOemOriginal === true}
                onChange={() => setIsOemOriginal(true)}
              />
              Yes
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                name="oem-original"
                checked={isOemOriginal === false}
                onChange={() => {
                  setIsOemOriginal(false);
                  setOemPhotoUrl('');
                  setOemDowngradeMsg('');
                }}
              />
              No
            </label>
          </div>
          {isOemOriginal && (
            <OemLabelCapture
              partName={partName}
              grade={grade}
              value={oemPhotoUrl}
              onChange={({ oemPhotoUrl: url }) => setOemPhotoUrl(url)}
              onDowngrade={({ message }) => setOemDowngradeMsg(message)}
            />
          )}
          {oemDowngradeMsg && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2">
              {oemDowngradeMsg}
            </p>
          )}
          {fieldErrors.oem && <p className="text-xs text-red-600">{fieldErrors.oem}</p>}
        </section>

        <section className="space-y-3">
          {provenanceRequired && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-3">
              <p className="text-sm font-semibold text-amber-900 flex items-center gap-2">
                <FaExclamationTriangle /> Enhanced Provenance Pack required
              </p>
              <p className="text-xs text-amber-800">
                Restricted category — admin review before listing goes live.
              </p>
              <label className="block text-xs font-semibold text-slate-700">
                Source document *
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  className="mt-1 block w-full text-xs"
                  onChange={(e) => uploadDoc(e.target.files[0], setProvenanceSourceUrl)}
                />
              </label>
              <label className="block text-xs font-semibold text-slate-700">
                VIN / chassis evidence (if applicable)
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  className="mt-1 block w-full text-xs"
                  onChange={(e) => uploadDoc(e.target.files[0], setProvenanceVinUrl)}
                />
              </label>
              <label className="flex items-start gap-2 text-xs text-slate-700">
                <input
                  type="checkbox"
                  checked={sellerDeclaration}
                  onChange={(e) => setSellerDeclaration(e.target.checked)}
                />
                I declare this part was legally sourced and the information provided is accurate.
              </label>
              {fieldErrors.declaration && (
                <p className="text-xs text-red-600">{fieldErrors.declaration}</p>
              )}
              <p className="text-xs text-slate-600 italic">
                Listing under review. Usually approved within 24h.
              </p>
            </div>
          )}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Availability tier</label>
            <select
              value={availabilityTier}
              onChange={(e) => setAvailabilityTier(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            >
              {AVAILABILITY_TIERS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label} ({t.slaHours}h SLA)
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
        </section>

        <button
          type="submit"
          disabled={loadingSuggest}
          className="w-full py-3 rounded-xl bg-emerald-600 text-white font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50 sticky bottom-4 shadow-lg"
        >
          <FaCheckCircle />
          Continue to pricing
        </button>
      </form>
    </div>
  );
}
